/**
 * AI の重要文の重要度（核心・重要・補足）を表す共通の UI
 * 色と印は本文の傍線（skim-highlight.ts の SKIM_LEVEL_STYLES）と同じものを使う
 */
import { cn } from '@/lib/utils'
import type { SkimLevel } from '@/store/useUISettingsStore'

import type { KeySentence } from './book-ai-position'
import { SKIM_LEVEL_LABELS, SKIM_LEVEL_STYLES } from './skim-highlight'

const SKIM_LEVEL_OPTIONS: { value: SkimLevel; label: string }[] = [
  { value: 1, label: '核心だけ' },
  { value: 2, label: '重要以上' },
  { value: 3, label: 'すべて' },
]

const LEVELS: SkimLevel[] = [1, 2, 3]

/** 「核心だけ／重要以上／すべて」の切り替え */
export const SkimLevelSegment = ({ value, onChange }: { value: SkimLevel; onChange: (level: SkimLevel) => void }) => (
  <div className="grid grid-cols-3 gap-1 rounded-lg bg-background p-1">
    {SKIM_LEVEL_OPTIONS.map((option) => (
      <button
        key={option.value}
        type="button"
        className={cn(
          'rounded-md py-1.5 text-xs',
          value === option.value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground',
        )}
        onClick={() => onChange(option.value)}
      >
        {option.label}
      </button>
    ))}
  </div>
)

/** 重要度の丸印（本文では補足に印を付けないが、一覧では見分けられるよう「補」を出す） */
export const SkimLevelBadge = ({ level, className }: { level: SkimLevel; className?: string }) => {
  const style = SKIM_LEVEL_STYLES[level]
  return (
    <span
      className={cn('flex size-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold', className)}
      style={{ backgroundColor: style.color, color: style.badgeText }}
      aria-label={SKIM_LEVEL_LABELS[level]}
    >
      {style.badge ?? SKIM_LEVEL_LABELS[level].charAt(0)}
    </span>
  )
}

/** 本文での見た目と同じ傍線と印の見本 */
export const SkimLegend = () => (
  <div className="flex gap-4 text-xs text-muted-foreground">
    {LEVELS.map((level) => {
      const style = SKIM_LEVEL_STYLES[level]
      return (
        <span key={level} className="flex items-center gap-1">
          {style.badge ? <SkimLevelBadge level={level} /> : <span className="size-4" />}
          <span className="w-5 rounded-full" style={{ height: style.thickness, backgroundColor: style.color }} />
          {SKIM_LEVEL_LABELS[level]}
        </span>
      )
    })}
  </div>
)

/** 重要文の一覧（重要度の印付き。押すとその箇所へ移動する） */
export const KeyPointList = ({ points, onNavigate }: { points: KeySentence[]; onNavigate: (key: number) => void }) => (
  <ul className="space-y-1">
    {points.map((point, i) => (
      <li key={`${point.key}-${i}`}>
        <button
          type="button"
          className="flex w-full items-start gap-2 rounded-lg p-2 text-left text-sm hover:bg-muted"
          onClick={() => onNavigate(point.key)}
        >
          <SkimLevelBadge level={point.level} className="mt-0.5" />
          <span className="line-clamp-3 wrap-break-word">{point.text}</span>
        </button>
      </li>
    ))}
  </ul>
)
